import van, {type State, type ValidChildDomValue} from "vanjs-core"
import {LoginState} from "@login/loginState";
import {LoginPanel} from "@login/loginPanel";
import {LoginLocationSelector} from "@login/loginLocationSelector";
import {Location} from "@orgs/general/location";
import {pageState, type StateEventDetail} from "@commons/layout/pageState";
import {Net} from "@utils/net";
const {div, aside, span, p} = van.tags;
import {HtmlTitle} from "@commons/site/htmlTitle";
import {UserLogin} from "@commons/site/userLogin";
import {LoginUtil} from "@login/loginUtil";
import {VanComponent} from "@utils/vanComponent";
import type {LoginPageResponse} from "pnyx-dto";
import '@utils/importCss';
import type {HolLoginResponse} from "hol-dto";
import '@login/login.css';
import {Icon} from "@commons/components/icons/icon";
import {ForgotPasswordSendCode, type IForgotPasswordSendCodeParent} from "@login/forgotPassword/forgotPasswordSendCode.ts";
import type {SendResetPasswordCodeResponse} from "pnyx-dto";
import {
    ForgotPasswordPromptCode,
    type IForgotPasswordPromptCodeParent
} from "@login/forgotPassword/forgotPasswordPromptCode.ts";
import type {VerifyResetPasswordCodeResponse} from "pnyx-dto";
import {
    ForgotPasswordChangePassword,
    type IForgotPasswordChangePasswordParent
} from "@login/forgotPassword/forgotPasswordChangePassword.ts";

export class LoginPage extends VanComponent implements IForgotPasswordSendCodeParent, IForgotPasswordPromptCodeParent, IForgotPasswordChangePasswordParent
{
    loginPanel: LoginPanel;
    mainPanel: State<VanComponent>;
    
    constructor() 
    {
        super();
        
        this.loginPanel = new LoginPanel();
        this.mainPanel = van.state<VanComponent>(this.loginPanel);
    }
    
    toDom(): HTMLElement
    {
        return div(this.toDomTags({ class: 'login-page' }),
            div({"class": "d-flex pt-3", "style": "min-height: 90px" },
                div({ class: "flex-grow-1"}),
                () => loginState.alert.toDom(),
                div({ class: "flex-grow-1"})
            ),
            div({"class": "d-flex"},
                div({ class: "flex-grow-1"}),
                this.toDomFrame(),
                div({ class: "flex-grow-1"}),
            )
        );
    }
    
    toDomFrame(): HTMLElement
    {
        return div(this.toDomTags({"class": "login-card"}),
            aside({class: 'login-brand'},
                span({ class: 'login-brand__mark'},
                    span('HOLOS'),
                    Icon.infinity().left().toDom()
                ),
                p({ class: 'login-brand__message' },
                    'Connected Care',
                ),
                span({ class: 'login-brand__detail', 'aria-hidden': 'true' })
            ),
            () => this.mainPanel.val.toDom() as ValidChildDomValue,            
        );        
    }

    onLogin(loginResponse: HolLoginResponse): void
    {
        if (loginResponse.applicationName)
            HtmlTitle.applicationName = loginResponse.applicationName;
        
        if (loginResponse.hasError)
        {
            loginState.alert.show(loginResponse.errorMessage);
            return;
        }
        
        if (loginResponse.locationList.length > 0)
        {
            this.mainPanel.val = new LoginLocationSelector(loginResponse.locationList);
            return;
        }

        LoginUtil.resetLocalStorage(loginResponse);
        window.location.href = loginResponse.url ?? '/';
    }

    onLocation(loginResponse: HolLoginResponse): void
    {
        if (loginResponse.hasError)
        {     
            loginState.alert.show(loginResponse.errorMessage);
            return;
        }

        LoginUtil.resetLocalStorage(loginResponse);
        window.location.href = loginResponse.url ?? '/';
    }
    
    onReturnToSignIn(userName: string = ''): void
    {
        loginState.alert.hide();

        this.loginPanel = new LoginPanel({ userName });
        this.mainPanel.val = this.loginPanel;
    }

    onPasswordChanged(userName: string): void
    {
        loginState.alert.show({ type: 'alert-success', 
            title: 'Password Changed',
            message: 'Your password has been changed. Please log in with your new password.' 
        });

        this.loginPanel = new LoginPanel({ userName });
        this.mainPanel.val = this.loginPanel;
    }

    onPromptUserForCode(codeResponse: SendResetPasswordCodeResponse): void
    {
        loginState.alert.hide();
        
        let panel: ForgotPasswordPromptCode = new ForgotPasswordPromptCode({ data: codeResponse, parent: this });
        this.mainPanel.val = panel;
    }

    onChangePassword(codeResponse: VerifyResetPasswordCodeResponse): void
    {
        loginState.alert.hide();

        let panel: ForgotPasswordChangePassword = new ForgotPasswordChangePassword({ data: codeResponse, parent: this });
        this.mainPanel.val = panel;
    }

    onForgotPasswordClick(): void
    {
        loginState.alert.hide();
        
        let panel: ForgotPasswordSendCode = new ForgotPasswordSendCode({ userName: this.loginPanel.userName, parent: this });
        this.mainPanel.val = panel;
    }
    
    async setState(state: any): Promise<void>
    {
        let url = window.location.pathname.toLowerCase();
        if (url.startsWith('/login/loc-'))
            url = url.replace('/loc-', '/getLoginPage-');
        else
            url = '/login/getLoginPage/';
        
        let loginPageResponse: LoginPageResponse = await Net.get<LoginPageResponse>({ 
                url: url, 
                queryParams: { returnUrl: state.returnUrl, locationUrlSegment: state.locationUrlSegment } 
            });
        
        this.loginPanel.model = loginPageResponse;
        loginState.model = loginPageResponse;
        HtmlTitle.applicationName = loginPageResponse.applicationName;
        HtmlTitle.pageTitle = 'Login';
    }
}

// Clears browser persisted log-in info
UserLogin.instance = new UserLogin();
Location.instance.orgLocation = null;

let loginState = LoginState.instance = new LoginState(new LoginPage());
loginState.registerListeners();

pageState.registerPage((detail: StateEventDetail): Promise<void> => loginState.loginPage.setState(detail.state));

van.add(document.body, loginState.loginPage.toDom());